/**
 * WidgetConfigForm — reusable config form for the Chanl chat widget.
 *
 * ───────────────────────────────────────────────────────────────────────────
 *  COPY-PASTE FRIENDLY
 *  This file is intentionally self-contained:
 *    • No imports from chat-host's iframe/ directory
 *    • No imports from widget-sdk internals beyond window.ChanlChat typing
 *    • Only stdlib React + inline styles (no Tailwind, no shadcn, no CSS modules)
 *
 *  To use it in another app (chanl-admin, chanl-site, customer integrations):
 *    1. Copy this file wherever you want the config form to live
 *    2. Mount <WidgetConfigForm /> — it handles form state + ChanlChat init/update
 *    3. Ensure `/widget/v1.js` is reachable (or set `chatHost` / script tag yourself)
 *    4. Make sure `window.ChanlChat` type is declared in your app
 *
 *  API:
 *    <WidgetConfigForm
 *      initialValues={...}        // optional URL param / cached state seed
 *      onChange={(form) => ...}   // optional — fires on every field change
 *      onApply={(form) => ...}    // optional — fires when Apply/init happens
 *      storageKey="..."           // override localStorage key (default shown below)
 *    />
 * ───────────────────────────────────────────────────────────────────────────
 */

import { useEffect, useMemo, useRef, useState } from 'react';

// ── Window typing for the embed loader ──────────────────────────────────

declare global {
  interface Window {
    ChanlChat?: {
      init: (config: Record<string, unknown>) => void;
      update?: (partial: Record<string, unknown>) => void;
      destroy?: () => void;
      open?: () => void;
      close?: () => void;
      toggle?: () => void;
      sendMessage?: (text: string) => void;
      setUser?: (user: Record<string, unknown>) => void;
      setContext?: (ctx: Record<string, unknown>) => void;
      _initialized?: boolean;
    };
  }
}

// ── Form shape ──────────────────────────────────────────────────────────
// Mirrors ChanlChatInitConfig (widget-sdk/src/embed/loader.ts). Every
// optional field is a STRING (empty = "don't send it to init"). Booleans
// use tri-state (default / true / false).

export type WidgetPosition = 'bottom-right' | 'bottom-left';
export type WidgetTheme = 'dark' | 'light';
export type BoolTri = 'default' | 'true' | 'false';

export interface WidgetConfigFormState {
  agentId: string;
  apiKey: string;
  theme: WidgetTheme;
  color: string;
  position: WidgetPosition;
  darkBg: string;
  agentName: string;
  agentSubtitle: string;
  agentInitials: string;
  agentAvatarUrl: string;
  greeting: string;
  placeholder: string;
  locale: string;
  privacyUrl: string;
  branding: BoolTri;
  proactiveMessages: string;
  chatHost: string;
  baseUrl: string;
  liveApply: boolean;
  autoOpen: boolean;
}

export const WIDGET_FORM_DEFAULTS: WidgetConfigFormState = {
  agentId: '',
  apiKey: '',
  theme: 'dark',
  color: '#c0673b',
  position: 'bottom-right',
  darkBg: '',
  agentName: '',
  agentSubtitle: '',
  agentInitials: '',
  agentAvatarUrl: '',
  greeting: '',
  placeholder: '',
  locale: '',
  privacyUrl: '',
  branding: 'default',
  proactiveMessages: '',
  chatHost: '',
  baseUrl: '',
  liveApply: false,
  autoOpen: true,
};

const REINIT_FIELDS = ['agentId', 'apiKey', 'chatHost', 'baseUrl'] as const;

function diffReinitFields(prev: WidgetConfigFormState, next: WidgetConfigFormState): boolean {
  return REINIT_FIELDS.some((k) => prev[k] !== next[k]);
}

/** Build the payload for `ChanlChat.init()` from the form state. */
export function formToInitConfig(form: WidgetConfigFormState): Record<string, unknown> {
  const cfg: Record<string, unknown> = {
    agentId: form.agentId,
    apiKey: form.apiKey,
    theme: form.theme,
    position: form.position,
    color: form.color,
    chatHost: form.chatHost || (typeof window !== 'undefined' ? window.location.origin : ''),
  };

  if (form.darkBg) cfg.darkBg = form.darkBg;
  if (form.agentName) cfg.agentName = form.agentName;
  if (form.agentSubtitle) cfg.agentSubtitle = form.agentSubtitle;
  if (form.agentInitials) cfg.agentInitials = form.agentInitials;
  if (form.agentAvatarUrl) cfg.agentAvatarUrl = form.agentAvatarUrl;
  if (form.greeting) cfg.greeting = form.greeting;
  if (form.placeholder) cfg.placeholder = form.placeholder;
  if (form.locale) cfg.locale = form.locale;
  if (form.privacyUrl) cfg.privacyUrl = form.privacyUrl;
  if (form.branding !== 'default') cfg.branding = form.branding === 'true';
  if (form.baseUrl) cfg.baseUrl = form.baseUrl;

  const proactive = form.proactiveMessages
    .split('\n')
    .map((s) => s.trim())
    .filter(Boolean);
  if (proactive.length > 0) cfg.proactiveMessages = proactive;

  return cfg;
}

/** Build the payload for `ChanlChat.update()` — all live-updatable fields. */
export function formToUpdatePayload(form: WidgetConfigFormState): Record<string, unknown> {
  const p: Record<string, unknown> = {
    theme: form.theme,
    color: form.color,
    position: form.position,
    agentName: form.agentName,
    agentSubtitle: form.agentSubtitle,
    agentInitials: form.agentInitials,
    agentAvatarUrl: form.agentAvatarUrl,
    placeholder: form.placeholder,
    locale: form.locale,
    privacyUrl: form.privacyUrl,
    darkBg: form.darkBg,
  };
  if (form.branding !== 'default') p.branding = form.branding === 'true';
  const proactive = form.proactiveMessages
    .split('\n')
    .map((s) => s.trim())
    .filter(Boolean);
  if (proactive.length > 0) p.proactiveMessages = proactive;
  return p;
}

// ── Props ───────────────────────────────────────────────────────────────

export interface WidgetConfigFormProps {
  /** Seed values — typically from URL params or a prior session. */
  initialValues?: Partial<WidgetConfigFormState>;
  /** Called on every form change (useful for URL sync / parent-level state). */
  onChange?: (form: WidgetConfigFormState) => void;
  /** Called when a form change is "applied" — explicit click OR live-apply keystroke. */
  onApply?: (form: WidgetConfigFormState) => void;
  /** localStorage key for persisting form state. Empty string disables persistence. */
  storageKey?: string;
}

const DEFAULT_STORAGE_KEY = 'chanl-widget-config-form-v1';

// ── Component ───────────────────────────────────────────────────────────

export function WidgetConfigForm({
  initialValues,
  onChange,
  onApply,
  storageKey = DEFAULT_STORAGE_KEY,
}: WidgetConfigFormProps = {}) {
  // Seed: localStorage → initialValues → defaults.
  const initial: WidgetConfigFormState = useMemo(() => {
    let saved: Partial<WidgetConfigFormState> | null = null;
    if (storageKey && typeof window !== 'undefined') {
      try {
        const raw = window.localStorage.getItem(storageKey);
        if (raw) saved = JSON.parse(raw);
      } catch {
        // ignore
      }
    }
    return { ...WIDGET_FORM_DEFAULTS, ...saved, ...initialValues };
  }, [initialValues, storageKey]);

  const [form, setForm] = useState<WidgetConfigFormState>(initial);
  const [appliedConfig, setAppliedConfig] = useState<WidgetConfigFormState | null>(
    initial.agentId && initial.apiKey ? initial : null,
  );
  const [runtimeStatus, setRuntimeStatus] = useState<string>('');

  const update = <K extends keyof WidgetConfigFormState>(
    key: K,
    value: WidgetConfigFormState[K],
  ) => {
    setForm((prev) => {
      const next = { ...prev, [key]: value };
      if (storageKey && typeof window !== 'undefined') {
        try {
          window.localStorage.setItem(storageKey, JSON.stringify(next));
        } catch {
          // ignore
        }
      }
      onChange?.(next);
      if (next.liveApply && next.agentId && next.apiKey) {
        setAppliedConfig(next);
        onApply?.(next);
      }
      return next;
    });
  };

  const canInit = form.agentId.trim().length > 0 && form.apiKey.trim().length > 0;
  const isDirty = appliedConfig ? JSON.stringify(form) !== JSON.stringify(appliedConfig) : canInit;

  const handleApply = (e: React.FormEvent) => {
    e.preventDefault();
    if (!canInit) return;
    setAppliedConfig(form);
    onApply?.(form);
  };

  const handleReset = () => {
    setForm(WIDGET_FORM_DEFAULTS);
    setAppliedConfig(null);
    setRuntimeStatus('');
    if (storageKey && typeof window !== 'undefined') window.localStorage.removeItem(storageKey);
  };

  const runAction = (label: string, fn: () => void) => {
    try {
      fn();
      setRuntimeStatus(`✓ ${label}`);
    } catch (err) {
      setRuntimeStatus(`✗ ${label}: ${err instanceof Error ? err.message : String(err)}`);
    }
    window.setTimeout(() => setRuntimeStatus(''), 2500);
  };

  // ── Mount + update the widget when appliedConfig changes ──
  // Deferred-destroy pattern: cleanup schedules destroy() via setTimeout. If
  // a new effect run fires before the timeout (React strict-mode double-mount,
  // or a config change re-run), the next effect cancels the pending destroy
  // and the widget stays alive. Only a REAL unmount (100ms of quiet) actually
  // tears down the widget.
  const destroyTimer = useRef<ReturnType<typeof setTimeout> | null>(null);
  const appliedRef = useRef<WidgetConfigFormState | null>(null);

  useEffect(() => {
    if (!appliedConfig) return;

    // Cancel any pending destroy from a previous cleanup
    if (destroyTimer.current) {
      clearTimeout(destroyTimer.current);
      destroyTimer.current = null;
    }

    const apply = () => {
      const prev = appliedRef.current;
      if (prev && !diffReinitFields(prev, appliedConfig) && window.ChanlChat?._initialized) {
        // Live update via update() — no iframe reload
        window.ChanlChat?.update?.(formToUpdatePayload(appliedConfig));
      } else {
        // First run OR auth/host change — full init
        if (window.ChanlChat?.destroy && window.ChanlChat?._initialized) {
          try {
            window.ChanlChat.destroy();
          } catch {
            // ignore
          }
        }
        window.ChanlChat?.init(formToInitConfig(appliedConfig));
        // Auto-open if requested
        if (appliedConfig.autoOpen) {
          // Defer so init() finishes creating the iframe first
          window.setTimeout(() => {
            window.ChanlChat?.open?.();
          }, 50);
        }
      }
      appliedRef.current = appliedConfig;
    };

    // Load the embed loader script once per page.
    const scriptId = 'chanl-widget-loader';
    const existing = document.getElementById(scriptId) as HTMLScriptElement | null;
    if (existing && window.ChanlChat?.init) {
      apply();
    } else if (existing) {
      existing.addEventListener('load', apply, { once: true });
    } else {
      const script = document.createElement('script');
      script.id = scriptId;
      script.src = '/widget/v1.js';
      script.async = true;
      script.onload = apply;
      document.body.appendChild(script);
    }

    return () => {
      // Defer destroy — if re-run fires before the timeout, it cancels it.
      destroyTimer.current = setTimeout(() => {
        if (window.ChanlChat?.destroy && window.ChanlChat?._initialized) {
          try {
            window.ChanlChat.destroy();
          } catch {
            // ignore
          }
        }
        appliedRef.current = null;
        destroyTimer.current = null;
      }, 150);
    };
  }, [appliedConfig]);

  // ── Render ──────────────────────────────────────────────────────────

  return (
    <form onSubmit={handleApply}>
      <div style={{ marginBottom: '1.25rem' }}>
        <span
          style={{
            display: 'inline-block',
            background: form.color || '#c0673b',
            color: '#fff',
            padding: '0.25em 0.75em',
            borderRadius: 9999,
            fontSize: '0.7em',
            fontWeight: 600,
            letterSpacing: '0.04em',
          }}
        >
          CHANL WIDGET · PLAYGROUND
        </span>
        <h2 style={{ fontSize: '1.25rem', fontWeight: 700, margin: '0.6rem 0 0.2rem' }}>
          Widget configuration
        </h2>
        <p style={{ color: '#525252', fontSize: '0.8rem', margin: 0, lineHeight: 1.55 }}>
          Every option maps 1:1 to{' '}
          <code style={{ background: '#ededed', padding: '0.05em 0.3em', borderRadius: 3 }}>
            ChanlChat.init()
          </code>
          . Non-auth changes apply live via <code>ChanlChat.update()</code>.
        </p>
      </div>

      <Fieldset legend="Required">
        <Field label="Agent ID" required>
          <input
            type="text"
            value={form.agentId}
            onChange={(e) => update('agentId', e.target.value)}
            placeholder="ag_xxx"
            style={inputStyle}
          />
        </Field>
        <Field label="API Key (pub_xxx)" required>
          <input
            type="text"
            value={form.apiKey}
            onChange={(e) => update('apiKey', e.target.value)}
            placeholder="pub_xxx"
            style={inputStyle}
          />
        </Field>
      </Fieldset>

      <Fieldset legend="Appearance">
        <Field label="Theme">
          <Segment
            value={form.theme}
            options={['dark', 'light'] as const}
            accent={form.color}
            onChange={(v) => update('theme', v)}
          />
        </Field>
        <Field label="Primary color">
          <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
            <input
              type="color"
              value={form.color}
              onChange={(e) => update('color', e.target.value)}
              style={{
                width: 44,
                height: 36,
                border: '1px solid #e5e7eb',
                borderRadius: 8,
                padding: 2,
                background: '#fff',
              }}
            />
            <input
              type="text"
              value={form.color}
              onChange={(e) => update('color', e.target.value)}
              style={{ ...inputStyle, flex: 1 }}
            />
          </div>
        </Field>
        <Field label="Launcher position">
          <Segment
            value={form.position}
            options={['bottom-right', 'bottom-left'] as const}
            accent={form.color}
            onChange={(v) => update('position', v)}
          />
        </Field>
        <Field label="Dark theme background (optional)" hint="CSS gradient or color; only applied when theme = dark.">
          <input
            type="text"
            value={form.darkBg}
            onChange={(e) => update('darkBg', e.target.value)}
            placeholder="linear-gradient(145deg, #1a2332, #243447)"
            style={inputStyle}
          />
        </Field>
      </Fieldset>

      <Fieldset legend="Agent Identity Override">
        <Field label="Agent name" hint="Header title. Leave empty to use the agent's own name.">
          <input
            type="text"
            value={form.agentName}
            onChange={(e) => update('agentName', e.target.value)}
            placeholder="Customer Assistant"
            style={inputStyle}
          />
        </Field>
        <Field label="Subtitle">
          <input
            type="text"
            value={form.agentSubtitle}
            onChange={(e) => update('agentSubtitle', e.target.value)}
            placeholder="We usually reply within a minute"
            style={inputStyle}
          />
        </Field>
        <Field label="Avatar initials (1–2 chars)">
          <input
            type="text"
            value={form.agentInitials}
            onChange={(e) => update('agentInitials', e.target.value.slice(0, 2))}
            placeholder="CA"
            maxLength={2}
            style={{ ...inputStyle, maxWidth: 100 }}
          />
        </Field>
        <Field label="Avatar image URL">
          <input
            type="url"
            value={form.agentAvatarUrl}
            onChange={(e) => update('agentAvatarUrl', e.target.value)}
            placeholder="https://example.com/avatar.png"
            style={inputStyle}
          />
        </Field>
      </Fieldset>

      <Fieldset legend="Content">
        <Field label="Greeting message" hint="Requires re-init (auth fields included) to take effect.">
          <textarea
            value={form.greeting}
            onChange={(e) => update('greeting', e.target.value)}
            placeholder="Hi! I'm your virtual assistant…"
            rows={2}
            style={{ ...inputStyle, resize: 'vertical', fontFamily: 'inherit' }}
          />
        </Field>
        <Field label="Input placeholder">
          <input
            type="text"
            value={form.placeholder}
            onChange={(e) => update('placeholder', e.target.value)}
            placeholder="Message Customer Assistant…"
            style={inputStyle}
          />
        </Field>
        <Field label="Locale" hint="BCP-47 code.">
          <input
            type="text"
            value={form.locale}
            onChange={(e) => update('locale', e.target.value)}
            placeholder="en"
            style={{ ...inputStyle, maxWidth: 120 }}
          />
        </Field>
        <Field label="Privacy policy URL">
          <input
            type="url"
            value={form.privacyUrl}
            onChange={(e) => update('privacyUrl', e.target.value)}
            placeholder="https://chanl.ai/privacy"
            style={inputStyle}
          />
        </Field>
        <Field label="Show 'Powered by Chanl' branding">
          <Segment
            value={form.branding}
            options={['default', 'true', 'false'] as const}
            accent={form.color}
            onChange={(v) => update('branding', v)}
          />
        </Field>
      </Fieldset>

      <Fieldset
        legend="Proactive messages"
        hint="One message per line. Shown as toasts before the user opens the chat."
      >
        <textarea
          value={form.proactiveMessages}
          onChange={(e) => update('proactiveMessages', e.target.value)}
          placeholder={'👋 Hey! How can I help today?\nAsk me anything — I can search our KB.'}
          rows={3}
          style={{ ...inputStyle, resize: 'vertical', fontFamily: 'inherit' }}
        />
      </Fieldset>

      <Fieldset legend="Advanced (leave empty for defaults)">
        <Field
          label="chatHost"
          hint="Origin the iframe is loaded from. Default: same origin as this page."
        >
          <input
            type="text"
            value={form.chatHost}
            onChange={(e) => update('chatHost', e.target.value)}
            placeholder="http://localhost:3300"
            style={inputStyle}
          />
        </Field>
        <Field
          label="baseUrl"
          hint="chanl-platform API origin. Auto-detected in dev."
        >
          <input
            type="text"
            value={form.baseUrl}
            onChange={(e) => update('baseUrl', e.target.value)}
            placeholder="http://localhost:3100"
            style={inputStyle}
          />
        </Field>
      </Fieldset>

      <Fieldset legend="Playground behavior">
        <Field label="Live apply" hint="Push changes to the widget on every keystroke.">
          <label style={{ display: 'flex', alignItems: 'center', gap: 8, fontSize: '0.85rem' }}>
            <input
              type="checkbox"
              checked={form.liveApply}
              onChange={(e) => update('liveApply', e.target.checked)}
            />
            Apply changes as I type
          </label>
        </Field>
        <Field label="Auto-open panel on init" hint="Opens the chat panel immediately (demo-friendly).">
          <label style={{ display: 'flex', alignItems: 'center', gap: 8, fontSize: '0.85rem' }}>
            <input
              type="checkbox"
              checked={form.autoOpen}
              onChange={(e) => update('autoOpen', e.target.checked)}
            />
            Open chat panel after init
          </label>
        </Field>
      </Fieldset>

      <div style={{ display: 'flex', gap: 8, marginTop: '1rem' }}>
        <button
          type="submit"
          disabled={!canInit || (!isDirty && appliedConfig !== null)}
          style={{
            flex: 1,
            background: canInit ? form.color : '#d4d4d4',
            color: '#fff',
            border: 'none',
            padding: '0.65rem 1rem',
            borderRadius: 8,
            fontSize: '0.9rem',
            fontWeight: 600,
            cursor: canInit ? 'pointer' : 'not-allowed',
            opacity: !isDirty && appliedConfig !== null ? 0.6 : 1,
            transition: 'opacity 120ms',
          }}
        >
          {appliedConfig ? (isDirty ? 'Apply changes' : 'Applied') : 'Init widget'}
        </button>
        <button
          type="button"
          onClick={handleReset}
          style={{
            background: '#fff',
            color: '#525252',
            border: '1px solid #e5e7eb',
            padding: '0.65rem 1rem',
            borderRadius: 8,
            fontSize: '0.9rem',
            fontWeight: 500,
            cursor: 'pointer',
          }}
        >
          Reset
        </button>
      </div>

      {/* Runtime actions — only surface when widget is loaded */}
      {appliedConfig && (
        <div style={{ marginTop: '1.5rem', paddingTop: '1rem', borderTop: '1px solid #e5e7eb' }}>
          <div
            style={{
              fontSize: '0.68rem',
              fontWeight: 600,
              textTransform: 'uppercase',
              letterSpacing: '0.06em',
              color: '#737373',
              marginBottom: '0.5rem',
            }}
          >
            Runtime actions (via window.ChanlChat)
          </div>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(2, 1fr)', gap: 6 }}>
            <ActionButton label="open()" onClick={() => runAction('open', () => window.ChanlChat?.open?.())} />
            <ActionButton label="close()" onClick={() => runAction('close', () => window.ChanlChat?.close?.())} />
            <ActionButton label="toggle()" onClick={() => runAction('toggle', () => window.ChanlChat?.toggle?.())} />
            <ActionButton label="destroy()" onClick={() => runAction('destroy', () => window.ChanlChat?.destroy?.())} />
            <ActionButton
              label="sendMessage('hi')"
              onClick={() => runAction("sendMessage('hi')", () => window.ChanlChat?.sendMessage?.('hi'))}
            />
            <ActionButton
              label="setUser({id:42})"
              onClick={() => runAction('setUser', () => window.ChanlChat?.setUser?.({ id: 42, name: 'Test User' }))}
            />
            <ActionButton
              label="show toasts"
              onClick={() =>
                runAction('show toasts', () => {
                  // Re-push the form's proactive messages via update() so they
                  // animate in NOW even if the chat panel is open. The loader
                  // bypasses the open-check when called via update().
                  const list = form.proactiveMessages
                    .split('\n')
                    .map((s) => s.trim())
                    .filter(Boolean);
                  if (list.length === 0) {
                    throw new Error('Proactive messages textarea is empty');
                  }
                  window.ChanlChat?.update?.({ proactiveMessages: list });
                })
              }
            />
          </div>
          {runtimeStatus && (
            <div
              style={{
                marginTop: 8,
                padding: '0.4rem 0.6rem',
                borderRadius: 6,
                fontSize: '0.75rem',
                background: runtimeStatus.startsWith('✓') ? '#dcfce7' : '#fee2e2',
                color: runtimeStatus.startsWith('✓') ? '#166534' : '#991b1b',
              }}
            >
              {runtimeStatus}
            </div>
          )}
        </div>
      )}
    </form>
  );
}

// ── Form primitives ─────────────────────────────────────────────────────

function Fieldset({
  legend,
  hint,
  children,
}: {
  legend: string;
  hint?: string;
  children: React.ReactNode;
}) {
  return (
    <fieldset style={{ border: 'none', padding: 0, margin: '0 0 1.25rem' }}>
      <legend
        style={{
          fontSize: '0.68rem',
          fontWeight: 600,
          textTransform: 'uppercase',
          letterSpacing: '0.06em',
          color: '#737373',
          padding: 0,
          marginBottom: hint ? '0.25rem' : '0.5rem',
        }}
      >
        {legend}
      </legend>
      {hint && (
        <p style={{ fontSize: '0.72rem', color: '#9ca3af', margin: '0 0 0.5rem', lineHeight: 1.4 }}>
          {hint}
        </p>
      )}
      <div style={{ display: 'flex', flexDirection: 'column', gap: '0.65rem' }}>{children}</div>
    </fieldset>
  );
}

function Field({
  label,
  hint,
  required,
  children,
}: {
  label: string;
  hint?: string;
  required?: boolean;
  children: React.ReactNode;
}) {
  return (
    <label style={{ display: 'block' }}>
      <span
        style={{
          display: 'block',
          fontSize: '0.76rem',
          fontWeight: 500,
          color: '#374151',
          marginBottom: '0.3rem',
        }}
      >
        {label}
        {required && <span style={{ color: '#dc2626', marginLeft: 4 }}>*</span>}
      </span>
      {children}
      {hint && (
        <span style={{ display: 'block', marginTop: '0.3rem', fontSize: '0.7rem', color: '#9ca3af', lineHeight: 1.45 }}>
          {hint}
        </span>
      )}
    </label>
  );
}

function Segment<T extends string>({
  value,
  options,
  accent,
  onChange,
}: {
  value: T;
  options: readonly T[];
  accent: string;
  onChange: (v: T) => void;
}) {
  return (
    <div style={{ display: 'flex', gap: 6 }}>
      {options.map((opt) => (
        <button
          key={opt}
          type="button"
          onClick={() => onChange(opt)}
          style={{
            flex: 1,
            padding: '0.5rem 0.5rem',
            border: '1px solid',
            borderColor: value === opt ? accent : '#e5e7eb',
            background: value === opt ? accent : '#fff',
            color: value === opt ? '#fff' : '#1a1a1a',
            borderRadius: 8,
            fontSize: '0.78rem',
            fontWeight: 500,
            cursor: 'pointer',
            textTransform: 'capitalize',
            transition: 'all 120ms',
          }}
        >
          {opt}
        </button>
      ))}
    </div>
  );
}

function ActionButton({ label, onClick }: { label: string; onClick: () => void }) {
  return (
    <button
      type="button"
      onClick={onClick}
      style={{
        padding: '0.4rem 0.6rem',
        border: '1px solid #e5e7eb',
        background: '#fff',
        color: '#374151',
        borderRadius: 6,
        fontSize: '0.72rem',
        fontWeight: 500,
        fontFamily: 'ui-monospace, SFMono-Regular, Menlo, monospace',
        cursor: 'pointer',
        textAlign: 'left',
      }}
    >
      {label}
    </button>
  );
}

// ── Shared styles ───────────────────────────────────────────────────────

const inputStyle: React.CSSProperties = {
  width: '100%',
  background: '#ffffff',
  border: '1px solid #e5e7eb',
  borderRadius: 8,
  padding: '0.5rem 0.7rem',
  fontSize: '0.83rem',
  color: '#1a1a1a',
  outline: 'none',
  fontFamily: 'inherit',
  boxSizing: 'border-box',
};
